/**
 * DemoWalkthrough - top-level overlay (PROD-595, Sprint 4.3.7-fix).
 *
 * Mounts inside the AppShell. Wires the `useWalkthroughStore` to the
 * `<WalkthroughCoachmark>` card. Owns:
 *   - Manual relaunch: the TopBar's "Show walkthrough" button calls
 *     `openManual()`; this component simply renders whatever is in the
 *     store.
 *   - Tenant token interpolation: step 1's body contains the canonical
 *     `{tenant}` token. We resolve the active tenant's display name via
 *     the tenant-config registry and pass the interpolated body to the
 *     coachmark so the data file stays copy-only.
 *
 * Sprint 4.3.7-fix (founder direction 2026-05-08 01:35): the
 * walkthrough no longer auto-opens on first visit. The friction was
 * unwanted during demo drives - real users had to dismiss before they
 * could do anything. The tour is now opt-in only via the TopBar's
 * "Show walkthrough" button. We persist a `tour_seen` flag the first
 * time the component mounts so future "auto-show on first load"
 * logic can distinguish "never seen" from "explicit dismiss".
 *
 * HC6 a11y: see WalkthroughCoachmark - this component is a pass-through.
 *
 * HC1 two-mode operation: the overlay is mode-agnostic. The walkthrough
 * itself is the same in Demo and Live; step 4 explicitly references
 * the toggle so the user understands the difference.
 *
 * HC9 telemetry-real: every step view, completion, and dismissal
 * routes through the store actions which emit the strict events. This
 * component does NOT emit telemetry directly - the store is the
 * single source of truth so every test can drive the store and assert
 * on the real telemetry sink.
 *
 * HC5: zero em / en dashes.
 */
import { useEffect } from 'react';
import { useWalkthroughStore } from '../state/walkthrough.store';
import { useUserStore } from '../state/user.store';
import { useTenantStore } from '../state/tenant.store';
import { TENANT_DISPLAY_NAMES } from '../data/tenant-configs';
import {
  WALKTHROUGH_STEPS,
  WALKTHROUGH_TOTAL_STEPS,
  interpolateTenant,
} from '../data/walkthrough-script';
import { WalkthroughCoachmark } from './WalkthroughCoachmark';

/**
 * Sprint 4.3.7-fix: localStorage key recording that the walkthrough
 * has been mounted at least once for this (userId, tenantId). The
 * value is purely informational - the auto-show flow is gone, but
 * keeping the marker makes it cheap to bring auto-show back behind a
 * feature flag without flipping the persistence shape.
 */
const TOUR_SEEN_STORAGE_KEY_PREFIX = 'cps:walkthrough:seen';

export function DemoWalkthrough() {
  const isOpen = useWalkthroughStore((s) => s.isOpen);
  const currentStepIndex = useWalkthroughStore((s) => s.currentStepIndex);
  const next = useWalkthroughStore((s) => s.next);
  const prev = useWalkthroughStore((s) => s.prev);
  const dismiss = useWalkthroughStore((s) => s.dismiss);
  const hydrate = useWalkthroughStore((s) => s.hydrate);

  const userId = useUserStore((s) => s.userId);
  const tenantId = useTenantStore((s) => s.tenantId);

  // Sprint 4.3.7-fix: hydrate the persisted dismissed flag so manual
  // relaunch knows the user's prior state. The auto-show branch is
  // intentionally removed; the TopBar's "Show walkthrough" button is
  // now the sole entry point.
  useEffect(() => {
    hydrate();
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(
          `${TOUR_SEEN_STORAGE_KEY_PREFIX}:${userId}:${tenantId}`,
          String(Date.now()),
        );
      } catch {
        // Ignore. localStorage is best-effort here.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tenantId]);

  if (!isOpen) return null;

  const step = WALKTHROUGH_STEPS[currentStepIndex];
  if (!step) return null;

  const tenantDisplayName = TENANT_DISPLAY_NAMES[tenantId] ?? tenantId;
  const resolvedBody = interpolateTenant(step.body, tenantDisplayName);
  const resolvedTitle = step.title;

  return (
    <WalkthroughCoachmark
      step={step}
      resolvedTitle={resolvedTitle}
      resolvedBody={resolvedBody}
      stepIndex={currentStepIndex}
      totalSteps={WALKTHROUGH_TOTAL_STEPS}
      onNext={next}
      onPrev={prev}
      onSkip={dismiss}
    />
  );
}
